import { DualGateStatus } from '../../components/licensing'
import type { Entitlement, ImportResponse, Organisation, XeroInvoiceState } from '../../types'
import { ActivationStatsSummary } from './ActivationStatsSummary'
import { EntitlementsList } from './EntitlementsList'
import { ImportXeroSection } from './ImportXeroSection'
import { RecentPaymentsTable } from './RecentPaymentsTable'
import { useActivationState } from './use-activation-state'

interface ActivationTabProps {
  entitlements: Entitlement[]
  xeroInvoices: XeroInvoiceState[]
  xeroStates: Map<string, XeroInvoiceState>
  organisations: Organisation[]
  onRefresh: () => Promise<void>
  onCreateOrganisation: (xeroOrgId?: string) => void
}

interface ErrorAlertProps {
  error: string
  onDismiss: () => void
}

function ErrorAlert({ error, onDismiss }: ErrorAlertProps): React.ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-red-600 dark:text-red-400">{error}</p>
      <button
        onClick={onDismiss}
        className="mt-2 text-sm text-red-700 underline hover:no-underline dark:text-red-300"
      >
        Dismiss
      </button>
    </div>
  )
}

interface ActivationStatusPanelProps {
  selectedEntitlement: Entitlement | undefined
  selectedXeroState: XeroInvoiceState | null
  isProcessing: boolean
  onPassPaymentGate: () => void
  onPassLicenseGate: () => void
  onActivate: () => void
}

function ActivationStatusPanel(props: ActivationStatusPanelProps): React.ReactElement {
  const { selectedEntitlement, selectedXeroState, isProcessing } = props
  const { onPassPaymentGate, onPassLicenseGate, onActivate } = props
  if (!selectedEntitlement) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-8 text-center dark:border-stone-700 dark:bg-stone-800">
        <p className="text-stone-500 dark:text-stone-400">
          Select an entitlement to view activation status
        </p>
      </div>
    )
  }
  return (
    <DualGateStatus
      entitlement={selectedEntitlement}
      xeroInvoiceState={selectedXeroState}
      onPassPaymentGate={onPassPaymentGate}
      onPassLicenseGate={onPassLicenseGate}
      onActivate={onActivate}
      isLoading={isProcessing}
    />
  )
}

export function ActivationTab(props: ActivationTabProps): React.ReactElement {
  const { entitlements, xeroInvoices, xeroStates, organisations } = props
  const { onRefresh, onCreateOrganisation } = props
  const state = useActivationState({ entitlements, onRefresh })
  const selectedEntitlement = entitlements.find((entry) => entry.id === state.selectedEntitlementId)
  const selectedXeroState = state.selectedEntitlementId
    ? (xeroStates.get(state.selectedEntitlementId) ?? null)
    : null

  const handleImportSuccess = (_result: ImportResponse): void => {
    void state.finishImport()
  }
  const handleImportError = (error: Error): void => {
    state.setError(error.message)
  }
  return (
    <div className="space-y-6">
      {state.error !== null && <ErrorAlert error={state.error} onDismiss={state.dismissError} />}
      <ImportXeroSection
        show={state.showImportSection}
        toggleShow={state.toggleImportSection}
        importMode={state.importMode}
        setImportMode={state.setImportMode}
        onImportSuccess={handleImportSuccess}
        onImportError={handleImportError}
        onCreateOrganisation={onCreateOrganisation}
      />
      <RecentPaymentsTable xeroInvoices={xeroInvoices} organisations={organisations} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <EntitlementsList
            entitlements={entitlements}
            xeroStates={xeroStates}
            selectedEntitlementId={state.selectedEntitlementId}
            onSelect={state.setSelectedEntitlementId}
          />
        </div>
        <div className="lg:col-span-2">
          <ActivationStatusPanel
            selectedEntitlement={selectedEntitlement}
            selectedXeroState={selectedXeroState}
            isProcessing={state.isProcessing}
            onPassPaymentGate={() => {
              void state.gateActions.passPayment()
            }}
            onPassLicenseGate={() => {
              void state.gateActions.passLicense()
            }}
            onActivate={() => {
              void state.gateActions.activate()
            }}
          />
        </div>
      </div>
      <ActivationStatsSummary entitlements={entitlements} />
    </div>
  )
}
